import { useLazySrpFareFamilyQuery } from '@/src/api/rtkQuery/srpFareFamilyApi'
import { FareFamilyApiResponse, Plan } from '@/src/components/desktop/srp/fareFamily/types'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import { DeviceType } from '@/src/types/deviceType'
import { FlightCard } from '@/src/types/flight'
import { getCookie } from '@/src/utils/browser'
import { isFareFamilyEnabled } from '@/src/utils/srp/isFareFamilyEnabled'
import {
    fareFamilyRulePriceModifierOnwardLeg,
    fareFamilyRulePriceModifierReturnLeg
} from '@/src/utils/srp/RT/fareFamilyRulePriceModifier'
import { getFareFamilyRequestPayload } from '@/src/utils/srp/searchUtils/getFareFamilyRequest'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'
import { useGetSearchParamsObj } from '../../searchParams'
import useSearchResults from '../../useSearchResults'

const RT_FARE_FAMILY_ENABLED = 'RT_FARE_FAMILY_ENABLED' //cookie key

const useRTFareFamilyManager = (selectCard: (flight: FlightCard, selectedFareFamilyPlan: Plan | null) => void) => {
    const [triggerFareFamily, { isFetching, isLoading }] = useLazySrpFareFamilyQuery()
    const { triggerSrpEvent } = useClevertapForSrp()
    const t = useTranslations('createItineraryStates')
    const isFareFamilyLoading = isFetching || isLoading
    const fareFamilyLoadingMessage = t('loadingFareOptions')

    const { jsons, sid, cards, selectedJourneyLeg } = useSearchResults()

    const { urlQueryObj } = useGetSearchParamsObj()
    const [fareFamilyPopup, setFareFamilyPopup] = useState<{
        card: FlightCard
        fareFamilyData: FareFamilyApiResponse
    } | null>(null)

    const isFareFamilyFlowAllowedValue = useMemo(() => {
        const { isRtFareFamilyEnabled } = jsons || {}
        const isFareFamilyFlowAllowedFromCookie = getCookie(RT_FARE_FAMILY_ENABLED)
        return isFareFamilyFlowAllowedFromCookie === 'true' || isRtFareFamilyEnabled
    }, [jsons])

    const handleOpenFareFamily = useCallback(
        async (
            flight: FlightCard,
            deviceType: DeviceType,
            selectedOnwardFareFamilyPlan: Plan | null,
            selectedOnwardCard: FlightCard | null
        ) => {
            if (isFareFamilyEnabled(flight) && isFareFamilyFlowAllowedValue) {
                //create fare family request payload
                const payload = getFareFamilyRequestPayload(
                    flight,
                    urlQueryObj,
                    deviceType,
                    sid || '',
                    jsons!,
                    selectedJourneyLeg === 1
                )

                //trigger fare family query
                const { error, data } = await triggerFareFamily(payload, true)
                //if error or no solutions, make non fare family flow
                const fareRulesLength = data?.responseBody?.data?.solutions?.[0]?.fareFamilyFareRules?.length
                if (error || (fareRulesLength && fareRulesLength < 2)) {
                    if (error) {
                        triggerSrpEvent('Air_Failures', {
                            action_name: 'fare_family_fetch_failure',
                            action_type: 'API',
                            action_value:
                                (error && (error as any).data?.message) ||
                                (error as any)?.message ||
                                'Failed to fetch fare family',
                            flightNumber: flight.airlineCodes?.join('-') || 'N/A'
                        })
                    }
                    selectCard(flight, null)
                    return
                }
                // console.log('data', data)
                // console.log('selectedJourneyLeg', selectedJourneyLeg)
                // console.log('selectedOnwardFareFamilyPlan', selectedOnwardFareFamilyPlan)
                //if we have solutions, open fare family popup
                if (data) {
                    const modifiedFareFamilyData =
                        selectedJourneyLeg === 0
                            ? fareFamilyRulePriceModifierOnwardLeg({
                                  fareFamilyData: data,
                                  card: flight,
                                  returnCards: cards?.[1] || []
                              })
                            : fareFamilyRulePriceModifierReturnLeg({
                                  fareFamilyData: data,
                                  card: flight,
                                  selectedOnwardFareFamilyPlan: selectedOnwardFareFamilyPlan!,
                                  selectedOnwardCard: selectedOnwardCard!
                              })
                    setFareFamilyPopup({ card: flight, fareFamilyData: modifiedFareFamilyData })

                    //add event
                    try {
                        triggerSrpEvent?.('Air_UI_Action', {
                            action_name: 'RT-SRP-fare-family-popup-open',
                            flightNumber: flight.airlineCodes?.join('-') || 'N/A',
                            journey_leg: selectedJourneyLeg === 0 ? 'onward' : 'return',
                            action_type: 'Click'
                        })
                    } catch (error) {
                        console.error('Error triggerSrpEvent: RT-SRP-fare-family-popup-open', error)
                    }
                }
            } else {
                selectCard(flight, null)
            }
        },
        [triggerSrpEvent, triggerFareFamily, jsons, sid, urlQueryObj, selectCard, selectedJourneyLeg]
    )

    const closeFareFamilyPopup = useCallback(() => {
        setFareFamilyPopup(null)
    }, [])

    const handleSelectFareFamilyPlan = useCallback(
        async (card: FlightCard, plan: Plan | null) => {
            selectCard(card, plan)

            try {
                triggerSrpEvent?.('Air_UI_Action', {
                    action_name: 'RT-SRP-fare-family-plan-selected',
                    flightNumber: card.airlineCodes?.join('-') || 'N/A',
                    journey_leg: selectedJourneyLeg === 0 ? 'onward' : 'return',
                    action_type: 'Click',
                    fare_family_plan: plan?.title || 'N/A',
                    total_RT_price: plan?.price || 0,
                    price: plan?.unModifiedTotalPrice || 0
                })
            } catch (error) {
                console.error('Error triggerSrpEvent: RT-SRP-fare-family-plan-selected', error)
            }
        },
        [selectCard, selectedJourneyLeg]
    )

    return {
        fareFamilyPopup,
        closeFareFamilyPopup,
        isFareFamilyLoading,
        fareFamilyLoadingMessage,
        handleOpenFareFamily,
        handleSelectFareFamilyPlan
    }
}

export default useRTFareFamilyManager
